import {
  CancellationStatus,
  DocumentAiStateInitialData,
  DocumentAiStateUpdate,
  DocumentEngineMonitoring,
  DocumentLayout,
  Execution,
  ExecutionContext,
  QueryStatement,
  SortOrder,
  TabGroup,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import {
  SwitchActiveOutputPayload,
  SwitchDocumentActiveQueryStatementPayload,
} from "./types";

export enum DocumentActionType {
  SORT_DOCUMENTS = "SORT_DOCUMENTS",
  ADD_DOCUMENT = "ADD_DOCUMENT",
  REMOVE_DOCUMENT = "REMOVE_DOCUMENT",
  REMOVE_ALL_DOCUMENTS = "REMOVE_ALL_DOCUMENTS",
  SET_ACTIVE_DOCUMENT = "SET_ACTIVE_DOCUMENT",
  CHANGE_DOCUMENT_CONTEXT = "CHANGE_DOCUMENT_CONTEXT",
  INIT_DOCUMENT_EXECUTION = "INIT_DOCUMENT_EXECUTION",
  CANCEL_DOCUMENT_EXECUTION = "CANCEL_DOCUMENT_EXECUTION",
  UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS = "UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS",
  UPDATE_DOCUMENT_QUERY_STATEMENT = "UPDATE_DOCUMENT_QUERY_STATEMENT",
  SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT = "SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT",
  SWITCH_ACTIVE_OUTPUT_TAB = "SWITCH_ACTIVE_OUTPUT_TAB",
  CHANGE_DOCUMENT_LAYOUT = "CHANGE_DOCUMENT_LAYOUT",
  UPDATE_DOCUMENT_ENGINE_MONITORING = "UPDATE_DOCUMENT_ENGINE_MONITORING",
  REORDER_DOCUMENT = "REORDER_DOCUMENT",
  UPDATE_DOCUMENT_AI_STATE = "UPDATE_DOCUMENT_AI_STATE",
  ADD_DOCUMENT_AI_STATE = "ADD_DOCUMENT_AI_STATE",
  REMOVE_DOCUMENT_AI_STATE = "REMOVE_DOCUMENT_AI_STATE",
  SET_QUERY_STATEMENT_STATE = "SET_QUERY_STATEMENT_STATE",
  ADD_TAB_GROUP = "ADD_TAB_GROUP",
  UPDATE_TAB_GROUP = "UPDATE_TAB_GROUP",
  REMOVE_TAB_GROUP = "REMOVE_TAB_GROUP",
  REORDER_TAB_GROUP = "REORDER_TAB_GROUP",
  SET_DOCUMENT_TAB_GROUP = "SET_DOCUMENT_TAB_GROUP",
  TOGGLE_TAB_GROUP_COLLAPSED = "TOGGLE_TAB_GROUP_COLLAPSED",
}

export type DocumentsAction =
  | {
      type: DocumentActionType.SORT_DOCUMENTS;
      payload: SortOrder;
    }
  | {
      type: DocumentActionType.ADD_DOCUMENT;
      payload: WorkspaceDocument;
    }
  | {
      type: DocumentActionType.SET_ACTIVE_DOCUMENT;
      payload: string;
    }
  | {
      type: DocumentActionType.REMOVE_DOCUMENT;
      payload: string; // uuid
    }
  | {
      type: DocumentActionType.REMOVE_ALL_DOCUMENTS;
      payload: {
        exceptDocumentIds: string[];
      };
    }
  | {
      type: DocumentActionType.CHANGE_DOCUMENT_CONTEXT;
      payload: {
        documentId: string;
        context: Partial<ExecutionContext>;
      };
    }
  | {
      type: DocumentActionType.INIT_DOCUMENT_EXECUTION;
      payload: {
        documentId: string;
        execution: Execution;
      };
    }
  | {
      type: DocumentActionType.UPDATE_DOCUMENT_QUERY_STATEMENT;
      payload: {
        documentId: string;
        id: string;
        queryStatement: Partial<Omit<QueryStatement, "prevStatus">>;
      };
    }
  | {
      type: DocumentActionType.CANCEL_DOCUMENT_EXECUTION;
      payload: string; // documentId
    }
  | {
      type: DocumentActionType.SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT;
      payload: SwitchDocumentActiveQueryStatementPayload;
    }
  | {
      type: DocumentActionType.SWITCH_ACTIVE_OUTPUT_TAB;
      payload: SwitchActiveOutputPayload;
    }
  | {
      type: DocumentActionType.CHANGE_DOCUMENT_LAYOUT;
      payload: {
        documentId: string;
        layout: Partial<DocumentLayout>;
      };
    }
  | {
      type: DocumentActionType.UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS;
      payload: {
        documentId: string;
        queryStatementId: string;
        cancellationStatus: CancellationStatus;
      };
    }
  | {
      type: DocumentActionType.UPDATE_DOCUMENT_ENGINE_MONITORING;
      payload: {
        documentId: string;
        engineMonitoring: DocumentEngineMonitoring;
      };
    }
  | {
      type: DocumentActionType.REORDER_DOCUMENT;
      payload: {
        documentId: string;
        overDocumentId: string;
      };
    }
  | {
      type: DocumentActionType.ADD_DOCUMENT_AI_STATE;
      payload: {
        documentId: string;
        aiState: DocumentAiStateInitialData;
      };
    }
  | {
      type: DocumentActionType.REMOVE_DOCUMENT_AI_STATE;
      payload: {
        documentId: string;
      };
    }
  | {
      type: DocumentActionType.UPDATE_DOCUMENT_AI_STATE;
      payload: {
        documentId: string;
        newAiState: DocumentAiStateUpdate;
      };
    }
  | {
      type: DocumentActionType.SET_QUERY_STATEMENT_STATE;
      payload: {
        documentId: string;
        queryStatementIndex: number;
        data: Record<string, unknown>;
      };
    }
  | {
      type: DocumentActionType.ADD_TAB_GROUP;
      payload: {
        tabGroup: TabGroup;
        documentIds: string[];
      };
    }
  | {
      type: DocumentActionType.UPDATE_TAB_GROUP;
      payload: {
        tabGroupId: string;
        changes: Omit<TabGroup, "id" | "collapsed">;
      };
    }
  | {
      type: DocumentActionType.REMOVE_TAB_GROUP;
      payload: string; // tab group id
    }
  | {
      type: DocumentActionType.REORDER_TAB_GROUP;
      payload: {
        tabGroupId: string;
        overDocumentId: string;
      };
    }
  | {
      type: DocumentActionType.SET_DOCUMENT_TAB_GROUP;
      payload: {
        documentId: string;
        groupId: string | null;
        beforeTabGroupId?: string;
      };
    }
  | {
      type: DocumentActionType.TOGGLE_TAB_GROUP_COLLAPSED;
      payload: string; // tab group id
    };
